import { useState } from "react";
import { useForm } from "react-hook-form";
import { ScaleLoader } from "react-spinners";
import { ReactComponent as EyeOpen } from "assets/svgs/eye-open.svg";
import { ReactComponent as EyeClose } from "assets/svgs/eye-close.svg";

const INPUT_BASE =
  "w-full h-[44px] px-3 bg-[#F6F6F8] border rounded-[8px] text-[15px] text-gray-800 outline-none transition-colors duration-200 placeholder-gray-400";
const INPUT_OK =
  "border-[#DADADF] focus:border-brand-purple focus:shadow-[0_0_0_3px_rgba(123,94,167,0.12)]";
const INPUT_ERR = "border-[#E05252]";

const FieldLabel = ({ htmlFor, children }) => (
  <label
    htmlFor={htmlFor}
    className="block text-[12px] font-medium text-[#555] mb-[5px]"
  >
    {children} <span className="text-[#E05252]">*</span>
  </label>
);

const FieldError = ({ id, message }) =>
  message ? (
    <p id={id} role="alert" className="text-[#E05252] text-[11px] mt-1">
      {message}
    </p>
  ) : null;

const SignupForm = ({ onSubmit, loading }) => {
  const {
    watch,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const password = watch("password");
  const [hidePassword, setHidePassword] = useState(true);
  const [hideConfirmPassword, setHideConfirmPassword] = useState(true);

  return (
    <>
      <div className="mb-5">
        <h2 className="text-[20px] font-bold text-navy mb-1">
          Create your account
        </h2>
        <p className="text-xs text-gray-400">
          Already have one?{" "}
          <a
            href="/login"
            className="text-brand-purple font-semibold hover:underline"
          >
            Log in
          </a>
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        {/* First Name + Last Name side by side */}
        <div className="flex gap-2.5">
          <div className="flex-1">
            <FieldLabel htmlFor="firstName">First Name</FieldLabel>
            <input
              id="firstName"
              {...register("firstName", {
                required: "Required",
                minLength: { value: 2, message: "Min 2 characters" },
              })}
              placeholder="First name"
              aria-describedby={errors.firstName ? "firstName-error" : undefined}
              className={`${INPUT_BASE} ${errors.firstName ? INPUT_ERR : INPUT_OK}`}
            />
            <FieldError id="firstName-error" message={errors.firstName?.message} />
          </div>
          <div className="flex-1">
            <FieldLabel htmlFor="lastName">Last Name</FieldLabel>
            <input
              id="lastName"
              {...register("lastName", {
                required: "Required",
                minLength: { value: 2, message: "Min 2 characters" },
              })}
              placeholder="Last name"
              aria-describedby={errors.lastName ? "lastName-error" : undefined}
              className={`${INPUT_BASE} ${errors.lastName ? INPUT_ERR : INPUT_OK}`}
            />
            <FieldError id="lastName-error" message={errors.lastName?.message} />
          </div>
        </div>

        {/* Email */}
        <div className="mt-3">
          <FieldLabel htmlFor="email">Email Address</FieldLabel>
          <input
            id="email"
            type="email"
            {...register("email", {
              required: "Email is required",
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: "Invalid email format",
              },
            })}
            placeholder="Email"
            aria-describedby={errors.email ? "email-error" : undefined}
            className={`${INPUT_BASE} ${errors.email ? INPUT_ERR : INPUT_OK}`}
          />
          <FieldError id="email-error" message={errors.email?.message} />
        </div>

        {/* Password */}
        <div className="mt-3">
          <FieldLabel htmlFor="password">Password</FieldLabel>
          <div className="relative">
            <input
              id="password"
              type={hidePassword ? "password" : "text"}
              {...register("password", {
                required: "Password is required",
                pattern: {
                  value:
                    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~`])[A-Za-z\d!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~`]{8,}$/,
                  message:
                    "8+ chars with uppercase, lowercase, number & special character",
                },
              })}
              placeholder="Create a password"
              aria-describedby={errors.password ? "password-error" : undefined}
              className={`${INPUT_BASE} pr-10 ${errors.password ? INPUT_ERR : INPUT_OK}`}
            />
            <button
              type="button"
              tabIndex={-1}
              onClick={() => setHidePassword((s) => !s)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              aria-label={hidePassword ? "Show password" : "Hide password"}
            >
              {hidePassword ? <EyeOpen /> : <EyeClose />}
            </button>
          </div>
          <FieldError id="password-error" message={errors.password?.message} />
        </div>

        {/* Confirm Password */}
        <div className="mt-3">
          <FieldLabel htmlFor="confirmPassword">Confirm Password</FieldLabel>
          <div className="relative">
            <input
              id="confirmPassword"
              type={hideConfirmPassword ? "password" : "text"}
              {...register("confirmPassword", {
                required: "Please confirm your password",
                validate: (value) =>
                  value === password || "Passwords do not match",
              })}
              placeholder="Re-enter your password"
              aria-describedby={
                errors.confirmPassword ? "confirmPassword-error" : undefined
              }
              className={`${INPUT_BASE} pr-10 ${errors.confirmPassword ? INPUT_ERR : INPUT_OK}`}
            />
            <button
              type="button"
              tabIndex={-1}
              onClick={() => setHideConfirmPassword((s) => !s)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              aria-label={
                hideConfirmPassword ? "Show password" : "Hide password"
              }
            >
              {hideConfirmPassword ? <EyeOpen /> : <EyeClose />}
            </button>
          </div>
          <FieldError
            id="confirmPassword-error"
            message={errors.confirmPassword?.message}
          />
        </div>

        {/* CTA */}
        <button
          type="submit"
          disabled={loading}
          aria-busy={loading}
          className="w-full mt-5 h-12 rounded-[10px] bg-gradient-to-r from-pink-500 to-purple-600 text-white text-[15px] font-bold relative border border-gray-300 hover:from-pink-600 hover:to-purple-700 hover:-translate-y-px transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-70"
        >
          <span className={loading ? "opacity-0" : "opacity-100"}>
            Create Account →
          </span>
          {loading && (
            <ScaleLoader
              loading
              height={16}
              color="white"
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
            />
          )}
        </button>

        {/* Terms statement */}
        <p className="text-center text-[11px] text-[#BBBBBB] mt-3 leading-relaxed">
          By continuing, you accept our{" "}
          <a
            href="https://nirvascribe.com/terms-of-service"
            target="_blank"
            rel="noopener noreferrer"
            className="text-brand-purple hover:underline"
          >
            Terms of Service
          </a>{" "}
          &{" "}
          <a
            href="https://nirvascribe.com/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-brand-teal hover:underline"
          >
            Privacy Policy
          </a>
        </p>
      </form>
    </>
  );
};

export default SignupForm;
